// src/pages/Finances.js — cash, forecast, expenses (crm_settings/finances)
import { useState, useEffect, useMemo, useCallback } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  watchContacts,
  watchPaymentsCollection,
} from "../lib/db";
import { getPaymentTimestampMs, paymentAmountUsd } from "../lib/metrics";
import {
  eachMonthOfInterval,
  parse,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  isValid,
} from "date-fns";
import PageTabs from "../components/PageTabs";
import KpiCard from "../components/charts/KpiCard";
import DataQualityBar, { loadDataQuality } from "../components/DataQualityBar";
import { filterPayments } from "../lib/dataQualityFilters";
import { TrendingUp, DollarSign, Receipt, Landmark } from "lucide-react";

const FIN_REF = doc(db, "crm_settings", "finances");

function uid(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function monthStr(d) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
}

function parseMonthStr(s) {
  if (!s || typeof s !== "string") return null;
  const d = parse(s.length === 7 ? `${s}-01` : s, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : null;
}

function countMonthlyCharges(startMonthStr, endDate = new Date()) {
  const startD = parseMonthStr(startMonthStr);
  if (!startD) return 0;
  const start = startOfMonth(startD);
  const end = endOfMonth(endDate);
  if (start > end) return 0;
  try {
    return eachMonthOfInterval({ start, end }).length;
  } catch {
    return 0;
  }
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "forecast", label: "Expected jobs" },
  { id: "expenses", label: "Expenses" },
  { id: "cash", label: "Money in the bank" },
];

const FINANCE_VIEWS = [
  { id: "month", label: "This month" },
  { id: "quarter", label: "Quarter" },
  { id: "year", label: "Year" },
  { id: "lifetime", label: "Lifetime" },
];

export default function Finances({ initialTab = "overview" }) {
  const [tab, setTab] = useState(initialTab);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [cfg, setCfg] = useState({
    openingBalance: 0,
    bankAdjust: 0,
    bankBalanceOverride: null,
    expectedJobs: [],
    expenses: [],
    excludedPaymentIds: [],
    manualIncome: [],
  });
  const [saving, setSaving] = useState(false);
  const [financeView, setFinanceView] = useState("month");
  const [dataQuality, setDataQuality] = useState(() => loadDataQuality());

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const u1 = watchContacts(setContacts, () => {});
    const u2 = watchPaymentsCollection(setPayments, () => {});
    return () => {
      u1();
      u2();
    };
  }, []);

  useEffect(() => {
    return onSnapshot(FIN_REF, (snap) => {
      const d = snap.data() || {};
      setCfg((prev) => ({
        ...prev,
        openingBalance: typeof d.openingBalance === "number" ? d.openingBalance : 0,
        bankAdjust: typeof d.bankAdjust === "number" ? d.bankAdjust : 0,
        bankBalanceOverride: d.bankBalanceOverride == null || d.bankBalanceOverride === "" ? null : Number(d.bankBalanceOverride),
        expectedJobs: Array.isArray(d.expectedJobs) ? d.expectedJobs : [],
        expenses: Array.isArray(d.expenses) ? d.expenses : [],
        excludedPaymentIds: Array.isArray(d.excludedPaymentIds) ? d.excludedPaymentIds : [],
        manualIncome: Array.isArray(d.manualIncome) ? d.manualIncome : [],
      }));
    });
  }, []);

  const excludedSet = useMemo(() => new Set(cfg.excludedPaymentIds || []), [cfg.excludedPaymentIds]);

  const periodBounds = useMemo(() => {
    const now = new Date();
    if (financeView === "month") return { start: startOfMonth(now).getTime(), end: endOfMonth(now).getTime() };
    if (financeView === "quarter") return { start: startOfQuarter(now).getTime(), end: endOfQuarter(now).getTime() };
    if (financeView === "year") return { start: startOfYear(now).getTime(), end: endOfYear(now).getTime() };
    return { start: 0, end: Date.now() };
  }, [financeView]);

  const stripeInPeriod = useMemo(() => {
    const { included } = filterPayments(payments, contacts, dataQuality, excludedSet);
    return included.reduce((s, p) => {
      const t = getPaymentTimestampMs(p);
      if (t < periodBounds.start || t > periodBounds.end) return s;
      return s + paymentAmountUsd(p);
    }, 0);
  }, [payments, contacts, dataQuality, excludedSet, periodBounds]);

  const manualInPeriod = useMemo(() => {
    return (cfg.manualIncome || []).reduce((s, row) => {
      const d = parseMonthStr(row.month)?.getTime() || 0;
      if (d < periodBounds.start || d > periodBounds.end) return s;
      return s + (Number(row.amount) || 0);
    }, 0);
  }, [cfg.manualIncome, periodBounds]);

  const expensesInPeriod = useMemo(() => {
    let sum = 0;
    for (const e of cfg.expenses || []) {
      const amt = Number(e.amount) || 0;
      if (e.kind === "one_time") {
        const d = parseMonthStr(e.month || e.startMonth)?.getTime();
        if (d == null) continue;
        if (d >= periodBounds.start && d <= periodBounds.end) sum += amt;
      } else if (e.kind === "monthly") {
        const startD = parseMonthStr(e.startMonth);
        if (!startD) continue;
        const months = eachMonthOfInterval({ start: startOfMonth(startD), end: endOfMonth(new Date()) });
        for (const m of months) {
          const t = m.getTime();
          if (t >= periodBounds.start && t <= periodBounds.end) sum += amt;
        }
      }
    }
    return sum;
  }, [cfg.expenses, periodBounds]);

  const incomePeriod = stripeInPeriod + manualInPeriod;
  const profitPeriod = incomePeriod - expensesInPeriod;

  const lifetimeIn = useMemo(() => {
    const { included } = filterPayments(payments, contacts, dataQuality, excludedSet);
    return included.reduce((s, p) => s + paymentAmountUsd(p), 0);
  }, [payments, contacts, dataQuality, excludedSet]);

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      await setDoc(FIN_REF, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
    } finally {
      setSaving(false);
    }
  }, []);

  const expenseTotals = useMemo(() => {
    let oneTime = 0;
    let monthlyTotal = 0;
    for (const e of cfg.expenses || []) {
      const amt = Number(e.amount) || 0;
      if (e.kind === "one_time") {
        oneTime += amt;
      } else if (e.kind === "monthly") {
        const n = countMonthlyCharges(e.startMonth || monthStr(new Date()));
        monthlyTotal += amt * n;
      }
    }
    return { oneTime, monthlyTotal, out: oneTime + monthlyTotal };
  }, [cfg.expenses]);

  const estimatedBank = useMemo(() => {
    return (Number(cfg.openingBalance) || 0) + lifetimeIn - expenseTotals.out + (Number(cfg.bankAdjust) || 0);
  }, [cfg.openingBalance, cfg.bankAdjust, lifetimeIn, expenseTotals.out]);

  const displayBank = useMemo(() => {
    const o = cfg.bankBalanceOverride;
    if (o != null && Number.isFinite(Number(o))) return Number(o);
    return estimatedBank;
  }, [cfg.bankBalanceOverride, estimatedBank]);

  const forecastWeighted = useMemo(() => {
    return (cfg.expectedJobs || []).reduce((s, j) => {
      const a = Number(j.amount) || 0;
      const p = j.probability != null ? Number(j.probability) : 1;
      const w = p > 1 ? p / 100 : p;
      return s + a * Math.min(1, Math.max(0, w));
    }, 0);
  }, [cfg.expectedJobs]);

  // ── Expected job draft
  const [ejTitle, setEjTitle] = useState("");
  const [ejAmount, setEjAmount] = useState("");
  const [ejProb, setEjProb] = useState("0.7");
  const [ejMonth, setEjMonth] = useState(monthStr(new Date()));

  function addExpectedJob(e) {
    e.preventDefault();
    const row = {
      id: uid("ej"),
      title: ejTitle.trim() || "Job",
      amount: Math.max(0, Number(ejAmount) || 0),
      probability: Number(ejProb) || 0,
      month: ejMonth,
      notes: "",
    };
    persist({ expectedJobs: [...(cfg.expectedJobs || []), row] });
    setEjTitle("");
    setEjAmount("");
  }

  // ── Expense draft
  const [exLabel, setExLabel] = useState("");
  const [exAmount, setExAmount] = useState("");
  const [exKind, setExKind] = useState("one_time");
  const [exStartMonth, setExStartMonth] = useState(monthStr(new Date()));

  function addExpense(ev) {
    ev.preventDefault();
    const row = {
      id: uid("ex"),
      label: exLabel.trim() || "Expense",
      amount: Math.max(0, Number(exAmount) || 0),
      kind: exKind,
      startMonth: exKind === "monthly" ? exStartMonth : monthStr(new Date()),
    };
    persist({ expenses: [...(cfg.expenses || []), row] });
    setExLabel("");
    setExAmount("");
  }

  return (
    <div className="page-wrap" style={{ maxWidth: 960 }}>
      <header className="page-header">
        <h1 className="page-header-title">Finances</h1>
        <p className="page-header-desc">
          Forecast pipeline, record expenses, and track an estimated cash position. Payment totals come from your{" "}
          <code>payments</code> collection (same rules as Analytics — internal matches excluded).
        </p>
      </header>

      <DataQualityBar onChange={setDataQuality} />

      <div className="filter-bar" style={{ marginBottom: 12 }}>
        {FINANCE_VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`filter-chip ${financeView === v.id ? "active" : ""}`}
            onClick={() => setFinanceView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>

      <PageTabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "overview" && (
        <>
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <KpiCard
            label={`Income (${financeView})`}
            icon={DollarSign}
            accent="success"
            value={`$${Math.round(incomePeriod).toLocaleString()}`}
            comparison={`Stripe $${Math.round(stripeInPeriod).toLocaleString()} + manual`}
          />
          <KpiCard
            label={`Expenses (${financeView})`}
            icon={Receipt}
            accent="warning"
            value={`$${Math.round(expensesInPeriod).toLocaleString()}`}
            comparison="Tracked in Expenses tab"
          />
          <KpiCard
            label={`Profit (${financeView})`}
            icon={TrendingUp}
            value={`$${Math.round(profitPeriod).toLocaleString()}`}
            comparison="Income − expenses for period"
          />
          <KpiCard
            label="Est. cash position"
            icon={Landmark}
            accent="success"
            value={`$${Math.round(displayBank).toLocaleString()}`}
            comparison={`Lifetime in $${Math.round(lifetimeIn).toLocaleString()} (calibrated)`}
          />
        </div>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 8 }}>Calibrate Stripe payments</div>
          <p className="text-caption" style={{ marginBottom: 12 }}>
            Exclude test/development orders from totals. Toggle filters above, or exclude individual payments below.
          </p>
          <div className="table-wrap" style={{ maxHeight: 220, overflow: "auto" }}>
            <table>
              <thead>
                <tr><th>Payment</th><th>Amount</th><th>Date</th><th /></tr>
              </thead>
              <tbody>
                {payments.filter((p) => paymentAmountUsd(p) > 0).slice(0, 40).map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontSize: 12 }}>{String(p.id).slice(0, 16)}…</td>
                    <td>${Math.round(paymentAmountUsd(p)).toLocaleString()}</td>
                    <td style={{ fontSize: 12 }}>{new Date(getPaymentTimestampMs(p)).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => {
                          const ids = new Set(cfg.excludedPaymentIds || []);
                          if (ids.has(p.id)) ids.delete(p.id);
                          else ids.add(p.id);
                          persist({ excludedPaymentIds: [...ids] });
                        }}
                      >
                        {excludedSet.has(p.id) ? "Include" : "Exclude"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-caption" style={{ marginBottom: 16 }}>
          Add expenses under Expenses, expected revenue under Expected jobs, and tune cash under Money in the bank.
        </p>
        </>
      )}

      {tab === "forecast" && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>
            Expected jobs
          </div>
          <form onSubmit={addExpectedJob} style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 160px", fontSize: 12 }}>
              Title
              <input className="input" value={ejTitle} onChange={(e) => setEjTitle(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <label style={{ flex: "0 0 100px", fontSize: 12 }}>
              Amount ($)
              <input className="input" type="number" value={ejAmount} onChange={(e) => setEjAmount(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <label style={{ flex: "0 0 90px", fontSize: 12 }}>
              Prob. (0–1 or %)
              <input className="input" value={ejProb} onChange={(e) => setEjProb(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <label style={{ flex: "0 0 130px", fontSize: 12 }}>
              Target month
              <input className="input" type="month" value={ejMonth} onChange={(e) => setEjMonth(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              Add
            </button>
          </form>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Amount</th>
                  <th>Prob.</th>
                  <th>Month</th>
                  <th>Weighted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(cfg.expectedJobs || []).length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state" style={{ padding: "24px 16px" }}>
                        <div className="empty-state-title">No pipeline rows yet</div>
                        <p className="empty-state-desc">Add an expected job above to build your weighted forecast.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  cfg.expectedJobs.map((j) => {
                    const a = Number(j.amount) || 0;
                    const p = j.probability != null ? Number(j.probability) : 1;
                    const w = p > 1 ? p / 100 : p;
                    const ww = a * Math.min(1, Math.max(0, w));
                    return (
                      <tr key={j.id}>
                        <td>{j.title}</td>
                        <td>${a.toLocaleString()}</td>
                        <td>{p}</td>
                        <td>{j.month || "—"}</td>
                        <td>${Math.round(ww).toLocaleString()}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => persist({ expectedJobs: cfg.expectedJobs.filter((x) => x.id !== j.id) })}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "expenses" && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>
            Expenses
          </div>
          <form onSubmit={addExpense} style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 200px", fontSize: 12 }}>
              Label
              <input className="input" value={exLabel} onChange={(e) => setExLabel(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <label style={{ flex: "0 0 100px", fontSize: 12 }}>
              Amount ($)
              <input className="input" type="number" value={exAmount} onChange={(e) => setExAmount(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
            </label>
            <label style={{ flex: "0 0 140px", fontSize: 12 }}>
              Type
              <select className="input" value={exKind} onChange={(e) => setExKind(e.target.value)} style={{ marginTop: 4, width: "100%" }}>
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly recurring</option>
              </select>
            </label>
            {exKind === "monthly" ? (
              <label style={{ flex: "0 0 140px", fontSize: 12 }}>
                First month
                <input className="input" type="month" value={exStartMonth} onChange={(e) => setExStartMonth(e.target.value)} style={{ marginTop: 4, width: "100%" }} />
              </label>
            ) : null}
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              Add
            </button>
          </form>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Details</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(cfg.expenses || []).length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state" style={{ padding: "24px 16px" }}>
                        <div className="empty-state-title">No expenses yet</div>
                        <p className="empty-state-desc">Track one-time or monthly recurring costs below.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  cfg.expenses.map((x) => (
                    <tr key={x.id}>
                      <td>{x.label}</td>
                      <td>${(Number(x.amount) || 0).toLocaleString()}</td>
                      <td>{x.kind === "monthly" ? "Monthly" : "One-time"}</td>
                      <td style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {x.kind === "monthly" ? `from ${x.startMonth || "—"}` : "single charge"}
                      </td>
                      <td>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => persist({ expenses: cfg.expenses.filter((e) => e.id !== x.id) })}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "cash" && (
        <div className="card">
          <div className="section-title" style={{ marginBottom: 8 }}>
            Money in the bank
          </div>
          <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.5 }}>
            <strong>Estimate</strong> = opening balance + lifetime payments (excl. internal) − expenses tracked below + adjustment. Use{" "}
            <strong>override</strong> when you&apos;ve reconciled with your real bank statement.
          </p>
          <div style={{ display: "grid", gap: 14, maxWidth: 420 }}>
            <label style={{ fontSize: 12 }}>
              Opening balance ($)
              <input
                className="input"
                type="number"
                defaultValue={cfg.openingBalance}
                key={`ob-${cfg.openingBalance}`}
                onBlur={(e) => persist({ openingBalance: Number(e.target.value) || 0 })}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12 }}>
              Adjustment ($) — corrections, unmodeled items
              <input
                className="input"
                type="number"
                defaultValue={cfg.bankAdjust}
                key={`ba-${cfg.bankAdjust}`}
                onBlur={(e) => persist({ bankAdjust: Number(e.target.value) || 0 })}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12 }}>
              Manual bank balance override (leave empty for auto)
              <input
                className="input"
                type="number"
                defaultValue={cfg.bankBalanceOverride ?? ""}
                key={`bo-${cfg.bankBalanceOverride ?? "x"}`}
                placeholder={`Auto: ${Math.round(estimatedBank)}`}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  persist({ bankBalanceOverride: v === "" ? null : Number(v) });
                }}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
          </div>
          <div className="card card-sm" style={{ marginTop: 20, background: "var(--bg-elevated)" }}>
            <div className="kpi-card-label">Computed (before override)</div>
            <div className="kpi-card-value" style={{ marginTop: 8 }}>${Math.round(estimatedBank).toLocaleString()}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8, lineHeight: 1.5 }}>
              Opening {Math.round(cfg.openingBalance || 0).toLocaleString()} + payments {Math.round(lifetimeIn).toLocaleString()} − expenses{" "}
              {Math.round(expenseTotals.out).toLocaleString()} + adj {Math.round(cfg.bankAdjust || 0).toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
